/** sequenceNumber (API) или короткий алиас sN / sn. */
const readPointSequence = (point) => {
    if (point == null || typeof point !== "object") {
        return null;
    }
    const raw = point.sequenceNumber ?? point.sN ?? point.sn;
    if (raw == null || raw === "") {
        return null;
    }
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
};

const hasSequenceNumber = (points) => (
    Array.isArray(points)
    && points.some((point) => readPointSequence(point) != null)
);

const sameXy = (a, b, eps = 0.05) => (
    a != null
    && b != null
    && Math.abs((Number(a.x) || 0) - (Number(b.x) || 0)) <= eps
    && Math.abs((Number(a.y) || 0) - (Number(b.y) || 0)) <= eps
);

const pointDist = (a, b) => Math.hypot(
    (Number(a?.x) || 0) - (Number(b?.x) || 0),
    (Number(a?.y) || 0) - (Number(b?.y) || 0),
);

const readEdgeSequence = (edge) => {
    if (edge == null) {
        return null;
    }
    const raw = edge.sequenceNumber ?? edge.sN ?? edge.sn;
    if (raw == null || raw === "") {
        return null;
    }
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
};

/**
 * Упорядочивает точки контура по sequenceNumber / sN на точках.
 * Если поля нет — порядок массива не меняется.
 */
export const orderContourPoints = (points) => {
    if (!points?.length) {
        return points ?? [];
    }
    if (!hasSequenceNumber(points)) {
        return points;
    }

    const ordered = [...points].sort((a, b) => {
        const sa = readPointSequence(a);
        const sb = readPointSequence(b);
        if (sa == null && sb == null) {
            return 0;
        }
        if (sa == null) {
            return 1;
        }
        if (sb == null) {
            return -1;
        }
        return sa - sb;
    });

    if (ordered.length < 2) {
        return ordered;
    }

    // Дуги (окружность / скругление): arcParams «принадлежит» стартовой точке ребра.
    // Перенос «замыкающей» точки ломает обход — оставляем только sort по sN.
    const hasArc = ordered.some((p) => p?.arcParams != null);
    if (hasArc) {
        return ordered;
    }

    const first = ordered[0];
    let closeIdx = -1;
    for (let i = ordered.length - 1; i >= 1; i -= 1) {
        if (sameXy(first, ordered[i])) {
            closeIdx = i;
            break;
        }
    }
    if (closeIdx > 0 && closeIdx < ordered.length - 1) {
        const [closing] = ordered.splice(closeIdx, 1);
        ordered.push(closing);
    }

    return ordered;
};

/** Уникальные точки по XY (для fallback, когда edges неполные). */
const uniquePointsByXy = (points) => {
    const out = [];
    for (const p of points) {
        if (!out.some((u) => sameXy(u, p))) {
            out.push(p);
        }
    }
    return out;
};

/**
 * Простой многоугольник без sequenceNumber: обход по углу вокруг центроида.
 * Подходит для выпуклых / почти выпуклых панелей (прямоугольник, срез угла).
 */
export const orderContourPointsByAngle = (points) => {
    const uniq = uniquePointsByXy(points ?? []);
    if (uniq.length < 3) {
        return points ?? [];
    }
    const cx = uniq.reduce((s, p) => s + (Number(p.x) || 0), 0) / uniq.length;
    const cy = uniq.reduce((s, p) => s + (Number(p.y) || 0), 0) / uniq.length;
    return [...uniq].sort((a, b) => {
        const aa = Math.atan2((Number(a.y) || 0) - cy, (Number(a.x) || 0) - cx);
        const bb = Math.atan2((Number(b.y) || 0) - cy, (Number(b.x) || 0) - cx);
        return aa - bb;
    });
};

/**
 * Контур из by-module-id: points часто без sequenceNumber и вразнобой,
 * порядок обхода — по edges[].sequenceNumber + геометрическая цепочка.
 *
 * Алгоритм: стартуем с ребра с минимальным sN, дальше идём к ребру,
 * чей startPoint = конец текущего (точка на расстоянии length).
 */
export const orderContourPointsFromEdges = (points, edges) => {
    if (!points?.length) {
        return null;
    }
    const usableEdges = (Array.isArray(edges) ? edges : []).filter(
        (edge) => edge?.startPoint != null && Number(edge.length) > 0,
    );
    if (usableEdges.length < 2) {
        return null;
    }

    const byId = new Map();
    for (const p of points) {
        if (p?.id != null) {
            byId.set(p.id, p);
        }
    }

    const resolvePoint = (ref) => {
        if (ref == null) {
            return null;
        }
        if (ref.id != null && byId.has(ref.id)) {
            return byId.get(ref.id);
        }
        return points.find((p) => sameXy(p, ref)) ?? ref;
    };

    const findEndPoint = (edge, prevPoint, remainingEdges) => {
        const start = resolvePoint(edge.startPoint);
        if (!start) {
            return null;
        }
        const len = Number(edge.length);
        const candidates = points.filter(
            (p) => (p.id == null || p.id !== start.id)
                && !sameXy(p, start)
                && !(prevPoint && sameXy(p, prevPoint))
                && Math.abs(pointDist(start, p) - len) <= 0.6,
        );
        if (!candidates.length) {
            return null;
        }
        // Конец ребра = startPoint следующего неиспользованного ребра
        const nextStarts = remainingEdges
            .map((e) => resolvePoint(e.startPoint))
            .filter(Boolean);
        const asNextStart = candidates.find((p) => (
            nextStarts.some((ns) => sameXy(ns, p) || (ns.id != null && ns.id === p.id))
        ));
        return asNextStart ?? candidates[0];
    };

    const sortedBySeq = [...usableEdges].sort((a, b) => {
        const sa = readEdgeSequence(a);
        const sb = readEdgeSequence(b);
        if (sa == null && sb == null) {
            return 0;
        }
        if (sa == null) {
            return 1;
        }
        if (sb == null) {
            return -1;
        }
        return sa - sb;
    });

    const ordered = [];
    const usedEdgeIds = new Set();
    let current = sortedBySeq[0];
    let prevPoint = null;

    while (current && !usedEdgeIds.has(current.id ?? current)) {
        usedEdgeIds.add(current.id ?? current);
        const start = resolvePoint(current.startPoint);
        if (start && (ordered.length === 0 || !sameXy(ordered[ordered.length - 1], start))) {
            ordered.push(start);
        }
        const remaining = usableEdges.filter((edge) => {
            const key = edge.id ?? edge;
            return !usedEdgeIds.has(key);
        });
        const end = findEndPoint(current, prevPoint, remaining);
        if (!end) {
            break;
        }
        prevPoint = start;
        current = remaining.find((edge) => {
            const edgeStart = resolvePoint(edge.startPoint);
            return sameXy(edgeStart, end)
                || (edgeStart?.id != null && end?.id != null && edgeStart.id === end.id);
        });
    }

    const uniqCount = uniquePointsByXy(points).length;
    // Полный обход: почти все уникальные вершины (допуск −1 на незамкнутость)
    if (ordered.length >= 3 && ordered.length >= uniqCount - 1) {
        return ordered;
    }
    return null;
};

/**
 * Точки контура детали:
 * 1) edges.sequenceNumber + геометрическая цепочка
 * 2) sequenceNumber на точках
 * 3) угол вокруг центроида — только для простых панелей (≤6 вершин),
 *    иначе вогнутые контуры (Полик и т.п.) дают «рваный» силуэт
 */
export const orderDetailContourPoints = (detail) => {
    const points = detail?.detailContour?.points;
    if (!points?.length) {
        return points ?? [];
    }
    const fromEdges = orderContourPointsFromEdges(points, detail?.edges);
    if (fromEdges?.length >= 3) {
        return fromEdges;
    }
    if (hasSequenceNumber(points)) {
        return orderContourPoints(points);
    }
    const uniq = uniquePointsByXy(points);
    if (uniq.length >= 3 && uniq.length <= 6) {
        return orderContourPointsByAngle(points);
    }
    return points;
};

/** Числовой sequenceNumber / sN с детали (для порядка панелей на сцене). */
export const readDetailSequenceNumber = (detail) => {
    if (detail == null || typeof detail !== "object") {
        return null;
    }
    const candidates = [
        detail.sequenceNumber,
        detail.sN,
        detail.sn,
        detail.SequenceNumber,
        detail.sequence_number,
        detail.detail?.sequenceNumber,
        detail.detail?.sN,
        detail.detail?.sn,
        detail.pacedDetailModule?.sequenceNumber,
        detail.pacedDetailModule?.sN,
        detail.pacedDetailModule?.sn,
    ];
    for (const raw of candidates) {
        if (raw == null || raw === "") {
            continue;
        }
        const num = Number(raw);
        if (Number.isFinite(num)) {
            return num;
        }
    }
    return null;
};

/**
 * Порядок детали на 3D-сцене: sequenceNumber / sN.
 * Если поля нет — index в списке (как раньше).
 */
export const getDetailSceneDrawOrder = (detail, index = 0) => {
    const seq = readDetailSequenceNumber(detail);
    return seq != null ? seq : index;
};

export const compareDetailsBySceneDrawOrder = (a, b, indexA = 0, indexB = 0) => {
    const diff = getDetailSceneDrawOrder(a, indexA) - getDetailSceneDrawOrder(b, indexB);
    if (diff !== 0) {
        return diff;
    }
    return indexA - indexB;
};

/** Стабильная сортировка списка деталей по sequenceNumber (sN). */
export const sortDetailsBySequenceNumber = (details) => {
    if (!Array.isArray(details) || details.length < 2) {
        return Array.isArray(details) ? details : [];
    }
    return details
        .map((detail, index) => ({detail, index}))
        .sort((a, b) => compareDetailsBySceneDrawOrder(a.detail, b.detail, a.index, b.index))
        .map(({detail}) => detail);
};
